import { ICustomizations, createTheme } from '@fluentui/react';
import { addAlphaChannelToHex } from '../../SubwayNav/customizations';
import { IM365ExtendedSemanticColors } from './M365ExtendedSemanticSlots.types';
import { M365TeamsStyles } from './teamsStyles';
import { M365Styles } from '.';

export namespace TeamsDarkColorPalette {
  export const themePrimary = '#9ea2ff';
  export const themeLighterAlt = '#8d90e3';
  export const themeLighter = '#7b7ec7';
  export const themeLight = '#6a6cab';
  export const themeTertiary = '#595a8f';
  export const themeSecondary = '#9ea2ff'; // Required by Fabric palette, only used in ShimmerWave
  export const themeDarkAlt = '#9ea2ff';
  export const themeDark = '#9ea2ff';
  export const themeDarker = '#9ea2ff';
  export const neutralLighterAlt = '#1e1e1e';
  export const neutralLighter = '#616161';
  export const neutralLight = '#1c1c1c';
  export const neutralQuaternaryAlt = '#323232';
  export const neutralQuaternary = '#3B3B3B';
  export const neutralTertiaryAlt = '#484848';
  export const neutralTertiary = '#c8c8c8';
  export const neutralSecondary = '#d0d0d0';
  export const neutralPrimaryAlt = '#dadada';
  export const neutralPrimary = '#ffffff';
  export const neutralDark = '#f4f4f4';
  export const black = '#f8f8f8';
  export const white = '#292929';

  // Warnings and Errors
  export const errorText = '#FF3C00';
  export const errorBackground = '#3B1E1E';
  export const blockingBackground = '#3B1E1E';
  export const warningBackground = '#30250A';
  export const warningHighlight = '#FFB900';
  export const successBackground = '#24290F';

  // Extended colors
  export const severeWarningBackground = '#3B200C'; // Actually an extension color

  export const dashboardBackdrop = '#303030';
  export const panelBackground = '#212121';
  export const overlayBackground = 'rgba(0,0,0,.4)';
  export const navBackground = '#212121';

  export const dataVis1 = '#31B0CD';
  export const dataVis2 = '#AE4689';
  export const dataVis3 = '#768DF1';
  export const dataVis4 = '#C9A618';
  export const dataVis5 = '#286EA8';
  export const dataVis6 = '#A43FB1';
  export const dataVis7 = '#EF8CCB';
  export const dataVis8 = '#218B8B';
  export const dataVis9 = '#A083C9';
  export const dataVis10 = '#9C663F';

  export const criticalStatus = '#FF0505';
  export const highStatus = '#FF3C00';
  export const mediumStatus = '#FFAA00';
  export const lowStatus = '#F2F200';

  // @deprecated, please use 'goodStatus' instead
  export const readyStatus = '#009E00';
  export const goodStatus = '#009E00';
  export const unkownStatus = '#6B6B6B';

  export const needHelpBackground = '#00838C';
  export const needHelpBackgroundPressed = '#009BA6';
  // @deprecated - will remain in place but may not be a valid value.
  export const contextualAlertBackground = '#C0A9FF';

  export const whiteTranslucent40 = 'rgba(20,20,20,.4)';

  export const tagBackgroundLowImpact = '#323130';
  export const tagBackgroundMediumImpact = '#484644';
  export const tagBackgroundActiveWarning = '#E60000';
}

const p = TeamsDarkColorPalette;
const primaryButtonTextOverride = '#FFFFFF';
const primaryButtonBackgroundOverride = '#5b5fc7';

const extendedSemanticColors: Partial<IM365ExtendedSemanticColors> = {
  // //////////////////////////////
  // /   Base mapping changes
  // //////////////////////////////
  bodyStandoutBackground: p.neutralQuaternaryAlt,
  bodyTextChecked: p.neutralDark,
  primaryButtonText: primaryButtonTextOverride,
  primaryButtonBackground: primaryButtonBackgroundOverride,
  primaryButtonTextHovered: p.black,
  primaryButtonTextPressed: p.black,
  primaryButtonTextDisabled: p.neutralTertiaryAlt,
  primaryButtonBackgroundDisabled: p.neutralLighter,
  accentButtonBackground: primaryButtonBackgroundOverride,
  accentButtonText: primaryButtonTextOverride,
  buttonBackground: `${addAlphaChannelToHex('#FFFFFF', 0)}`,
  buttonBorder: `${addAlphaChannelToHex('#FFFFFF', 30)}`,
  buttonBackgroundChecked: p.neutralLighter,
  buttonBackgroundHovered: `${addAlphaChannelToHex('#FFFFFF', 10)}`,
  buttonBackgroundCheckedHovered: p.neutralTertiaryAlt,
  buttonBackgroundPressed: `${addAlphaChannelToHex('#FFFFFF', 20)}`,
  buttonBackgroundDisabled: '#F0F0F0',
  buttonTextHovered: p.neutralPrimary,
  buttonTextDisabled: '#C7C7C7',
  inputForegroundChecked: p.white,
  menuHeader: p.neutralPrimary,
  menuIcon: p.themePrimary,
  listItemBackgroundChecked: p.neutralQuaternaryAlt,
  listItemBackgroundCheckedHovered: p.neutralQuaternary,
  link: p.themePrimary,
  linkHovered: p.themeLighter,

  // Message bar colors
  messageText: p.neutralPrimary,
  messageLink: p.themeDarkAlt,
  messageLinkHovered: p.themeDarker,

  infoIcon: p.neutralSecondary,
  errorIcon: p.highStatus,
  blockingIcon: p.highStatus,
  successIcon: p.goodStatus,
  warningIcon: p.neutralPrimary,
  severeWarningIcon: p.mediumStatus,

  infoBackground: p.neutralLighter,
  errorBackground: p.errorBackground,
  blockingBackground: p.blockingBackground,
  warningBackground: p.warningBackground,
  severeWarningBackground: p.severeWarningBackground,
  successBackground: p.successBackground,

  // Error and warnings
  errorText: p.errorText,
  warningHighlight: p.warningHighlight,
  warningText: p.neutralPrimary,

  // //////////////////////////////
  // Extended colors
  // //////////////////////////////

  dashboardBackdrop: p.dashboardBackdrop,
  gridItemDestinationBackground: p.neutralLighter,
  panelBackground: p.panelBackground,
  overlayBackground: p.overlayBackground,

  // Divider lines
  sectionDividerLine: p.neutralPrimary,
  primaryDividerLine: p.neutralTertiary,
  secondaryDividerLine: p.neutralTertiaryAlt,

  // Data vis
  dataVis1: p.dataVis1,
  dataVis2: p.dataVis2,
  dataVis3: p.dataVis3,
  dataVis4: p.dataVis4,
  dataVis5: p.dataVis5,
  dataVis6: p.dataVis6,
  dataVis7: p.dataVis7,
  dataVis8: p.dataVis8,
  dataVis9: p.dataVis9,
  dataVis10: p.dataVis10,

  // Text
  headerText: p.black,
  secondaryListText: p.neutralSecondary,

  // Nav
  navBackground: p.navBackground,
  navItemBackgroundHovered: p.neutralTertiaryAlt,
  navItemBackgroundPressed: p.neutralTertiary,

  // Status
  criticalStatus: p.criticalStatus,
  highStatus: p.highStatus,
  mediumStatus: p.mediumStatus,
  lowStatus: p.lowStatus,
  readyStatus: p.goodStatus,
  goodStatus: p.goodStatus,
  unkownStatus: p.unkownStatus,

  // Steps
  stepCompleted: p.themePrimary,
  stepCurrent: p.themePrimary,
  stepNotStarted: p.themePrimary,
  stepModifierBorder: p.themePrimary,
  stepHover: p.themePrimary,
  stepPressed: p.themePrimary,
  stepError: p.highStatus,
  allStepsComplete: p.goodStatus,
  /* tslint:disable-next-line:deprecation */
  contextualAlertBackground: p.contextualAlertBackground,

  // Help + Feedback
  needHelpForeground: p.black,
  needHelpBackground: p.needHelpBackground,
  needHelpBackgroundPressed: p.needHelpBackgroundPressed,
  feedbackForeground: p.white,
  feedbackBackground: '#c8c8c8',
  feedbackBackgroundPressed: p.neutralTertiary,

  // Because the Fabric controls bank on theme.palette.white/black inverting.
  alwaysWhite: p.black,
  alwaysBlack: p.white,

  // InPageFilter
  filterPillRest: p.neutralQuaternaryAlt,
  filterPillHover: p.neutralLight,
  filterPillPressed: p.neutralLighter,
  filterPillEditedRest: p.themeDarkAlt,
  filterPillEditedHover: p.themeDark,
  filterPillEditedPressed: p.themeDarker,

  // Tag
  tagBackgroundLowImpact: p.tagBackgroundLowImpact,
  tagBackgroundMediumImpact: p.tagBackgroundMediumImpact,
  tagBackgroundHighImpact: p.neutralPrimary,
  tagBackgroundNew: p.themePrimary,
  tagBackgroundActiveWarning: p.tagBackgroundActiveWarning,
  tagTextLowImpact: p.neutralPrimary,
  tagTextMediumImpact: p.neutralPrimary,
  tagTextHighImpact: p.white,
  tagTextNew: p.black,
  tagTextActiveWarning: p.black,
};

export namespace TeamsDarkThemeDepths {
  export const depth0 = '0 0 0 0 transparent';
  export const depth4 = '0 1.6px 3.6px 0 rgba(0, 0, 0, 0.66), 0 0.3px 0.9px 0 rgba(0, 0, 0, 0.54)';
  export const depth8 = '0 6.4px 14.4px 0 rgba(0, 0, 0, 0.66), 0 1.2px 3.6px 0 rgba(0, 0, 0, 0.54)';
  export const depth16 = '0 12.8px 28.8px 0 rgba(0, 0, 0, 0.66), 0 2.4px 7.2px 0 rgba(0, 0, 0, 0.54)';
  export const depth64 = '0 25.6px 40px 0 rgba(0, 0, 0, 0.66), 0 4.8px 14.4px 0 rgba(0, 0, 0, 0.54)';
}

export const TeamsDarkTheme: ICustomizations = {
  settings: {
    theme: createTheme({
      palette: { ...TeamsDarkColorPalette },
      effects: {
        elevation4: TeamsDarkThemeDepths.depth4,
        elevation8: TeamsDarkThemeDepths.depth8,
        elevation16: TeamsDarkThemeDepths.depth16,
        elevation64: TeamsDarkThemeDepths.depth64,
      },
      semanticColors: extendedSemanticColors,
    }),
  },
  scopedSettings: {},
};

// Re-exporting since the original is actually a customizations object.
export const TeamsDarkCustomizations: ICustomizations = TeamsDarkTheme;
export const TeamsDarkCustomizationsWithStyles: ICustomizations = {
  settings: TeamsDarkTheme.settings,
  scopedSettings: { ...M365Styles, ...M365TeamsStyles },
};

/**
 * @deprecated This value is exported solely for test purposes.
 * Please properly instantiate a theme instead of using this value directly.
 */
export const TeamsDarkExtendedSemanticColorsForTest = extendedSemanticColors;
